import { PRODUCT_ID_PREFIX } from "../constants";
import { Product } from "../types/model.types";

export const buildQueryParams = (params: Record<string, unknown>): string => {
  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null) return;

    if (Array.isArray(value)) {
      value.forEach((v) => searchParams.append(key, String(v)));
    } else {
      searchParams.append(key, String(value));
    }
  });

  return searchParams.toString();
};

// src/utils/toSlug.ts
export function toSlug(text: string) {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/gi, "-")
    .replace(/^-+|-+$/g, "");
}

export function productItemToSlug(item: Product) {
  const slugPart = item.urlKey || toSlug(item.name || "");
  const idPart = item.id.replace(PRODUCT_ID_PREFIX, "");
  const product_slug = `${slugPart}-${idPart}`;
  return product_slug;
}

export function hasRNWebViewMessaging(): boolean {
  if (typeof window === "undefined") return false;
  return !!(
    (
      window as Window & {
        ReactNativeWebView?: { postMessage: (msg: string) => void };
      }
    ).ReactNativeWebView &&
    typeof (
      window as Window & {
        ReactNativeWebView?: { postMessage: (msg: string) => void };
      }
    ).ReactNativeWebView?.postMessage === "function"
  );
}

const getDateRange = (
  from: number | undefined | null,
  to: number | undefined | null
): boolean => {
  if (from == null || to == null) return true;

  const now = Date.now();
  return now >= from && now <= to;
};

const getDiscountDateBadge = (
  price: number | undefined,
  specialPrice: number | undefined,
  fromDate: number | undefined,
  toDate: number | undefined
): boolean => {
  return Boolean(
    specialPrice != null &&
    price != null &&
    specialPrice < price &&
    getDateRange(fromDate, toDate)
  );
};

const getDiscountPercent = (
  price: number | undefined | null,
  specialPrice: number | undefined | null
): number | undefined => {
  if (price && specialPrice != null && specialPrice < price) {
    return Math.round(((price - specialPrice) / price) * 100);
  }
  return undefined;
};


const getPromotionLabels = (d: Product): string[] => {
  return (d?.promotions ?? []).map((promotion) => promotion.promotionName!);
};

export const getThumbnail = (d: Product): string => {
  if (d.thumbnail) {
    return d.thumbnail.replace(
      /\/uploads\/thumbnail_/,
      '/uploads/thumbnail-lg_'
    );
  }

  const firstImage = d.images && d.images?.length > 0 ? d.images[0] : '';
  const mediaImage = d.mediaGallery && d.mediaGallery?.length > 0 ? d.mediaGallery[0] : '';
  return d.thumbnail ?? firstImage ?? mediaImage ?? '';
};

export const mapRecommendationProductsToProductCards = (
  products: Product[]
): Product[] => {
  return products.map((item) => {
    const isDiscountBadge = getDiscountDateBadge(
      item.price,
      item.specialPrice,
      item?.specialPriceFromDate,
      item?.specialPriceToDate
    );
    const discountPercent = getDiscountPercent(item.price, item.specialPrice);

    return {
      id: item.id,
      title: String(item.title ?? ''),
      image: {
        src: String(getThumbnail(item) ?? ''),
        alt: String(item.title ?? 'Product'),
      },
      sameDay: false,
      deliveryTitle: '',
      price: isDiscountBadge ? item.specialPrice : item.price,
      oldPrice: item.specialPrice != null ? item.price : undefined,
      isDiscountBadge,
      discountLabel: isDiscountBadge
        ? "Save"
        : undefined,
      discountPercent,
      promotionalLabel: getPromotionLabels(item),
      info: item.info,
      productId: item.productId,
      sku: item.sku,
      inStock: item?.inStock,
      itemSoldBy: item?.itemSoldBy,
      brand: item?.brand,
      deliveryType: item?.deliveryType,
      itemMeasurementUnit: item?.itemMeasurementUnit,
      weight: item?.weight,
      quantity: item?.quantity,
      maxQuantityOfProduct: item?.maxQuantityOfProduct
    };
  });
};


export const getInStock = (
  d: Product,
): boolean => {
  return (
    (typeof d.inStock === 'number' && d.inStock > 0) ||
    (typeof d.isInStock === 'boolean' && d.isInStock) ||
    Boolean(d.productOnline)
  );
};